import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { organization } from "../../content/config";
import { isLocale, localizedPath } from "../../lib/routes";

const columns = [
  { title: "Organization", links: [["About", "about"], ["Transparency", "transparency"], ["Contact", "contact"]] },
  { title: "Work", links: [["Moldova", "moldova"], ["Israel", "israel"], ["Programs", "programs"], ["Insights", "insights"]] },
  { title: "Programs", links: [["Energy Resilience", "programs/energy-resilience"], ["Climate-Smart Agriculture", "programs/climate-smart-agriculture"], ["Green Wine Moldova", "programs/green-wine-moldova"], ["Energy Communities", "programs/energy-communities"]] },
  { title: "Legal", links: [["Privacy", "privacy"], ["Terms", "terms"]] }
];

const footerDescriptor = "Moldova · Israel · Sustainable Transition";

export function Footer() {
  const { locale } = useParams();
  const { t } = useTranslation();
  const activeLocale = isLocale(locale) ? locale : "en";

  return (
    <footer className="border-t border-line bg-bg-secondary">
      <div className="container-grid py-16 md:py-20">
        <div className="grid gap-12 lg:grid-cols-[1.5fr_repeat(4,1fr)]">
          <div className="max-w-sm">
            <Link to={localizedPath(activeLocale)} className="inline-flex items-center gap-3 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-[3px] focus-visible:ring-offset-bg-secondary">
              <span className="grid h-11 w-11 place-items-center">
                <img src="/branding/transition-bridge-mark.png" alt="Transition Bridge logo" width={44} height={44} className="h-11 w-11 object-contain" />
              </span>
              <span>
                <span className="block font-display text-lg font-extrabold tracking-tight">{organization.brandName}</span>
                <span className="block text-sm text-ink-secondary">{footerDescriptor}</span>
              </span>
            </Link>
            <p className="mt-6 text-sm leading-6 text-ink-secondary">Building implementation-ready sustainable transition projects between Moldova and Israel.</p>
            <Link to={localizedPath(activeLocale, "contact")} className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-brand-blue transition-colors hover:text-brand-blueDark">
              Start a conversation <ArrowRight className="rtl-flip h-4 w-4" aria-hidden="true" />
            </Link>
          </div>

          {columns.map((column) => (
            <div key={column.title}>
              <h2 className="mb-5 text-xs font-bold uppercase tracking-[0.14em] text-ink-secondary">{column.title}</h2>
              <ul className="space-y-3 text-sm font-medium text-ink-primary">
                {column.links.map(([label, path]) => (
                  <li key={path}>
                    <Link className="rounded-md transition-colors hover:text-brand-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-[3px] focus-visible:ring-offset-bg-secondary" to={localizedPath(activeLocale, path)}>{label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-16 flex flex-col gap-4 border-t border-line pt-7 text-sm text-ink-secondary md:flex-row md:items-center md:justify-between">
          <span>© {new Date().getFullYear()} {organization.registrationStatus === "registered" ? organization.legalNameEn : organization.brandName}</span>
          <div className="flex items-center gap-6">
            <Link className="rounded-md transition-colors hover:text-brand-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-[3px] focus-visible:ring-offset-bg-secondary" to={localizedPath(activeLocale, "privacy")}>Privacy</Link>
            <Link className="rounded-md transition-colors hover:text-brand-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-[3px] focus-visible:ring-offset-bg-secondary" to={localizedPath(activeLocale, "terms")}>Terms</Link>
            <span>{t("common.localeName")}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
